import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '@saleshandy/design-system';
import InlineAutosuggest, {
  InlineAutosuggestItem,
} from '../../../../../../../../../../shared/design-system/components/molecules/inline-autosuggest/inline-autosuggest';
import { getIsRequestPending } from '../../../../../../../../../../shared/utils';
import { LabelSettingsProps } from './types';
import SettingsText from '../settings-text';

const LabelSettings: React.FC<LabelSettingsProps> = ({
  activeNumberSettings,
  labelsList,
  getLabelsListRequestStatus,
  setPhoneNumberLabelRequestStatus,
  onSave,
}) => {
  const [selectedLabelName, setSelectedLabelName] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isNewLabel, setIsNewLabel] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const validateLabelName = useCallback((labelName: string) => {
    const trimmedLabel = labelName?.trim() || '';
    const isValidLength = trimmedLabel.length >= 2 && trimmedLabel.length <= 30;
    setHasError(!isValidLength && trimmedLabel.length > 0);
  }, []);

  useEffect(() => {
    if (hasError) {
      validateLabelName(selectedLabelName || '');
    }
  }, [selectedLabelName]);

  // Initialize label value from active number settings
  useEffect(() => {
    if (activeNumberSettings?.friendlyName) {
      setSelectedLabelName(activeNumberSettings.friendlyName);
      setIsNewLabel(false);
    }
  }, [activeNumberSettings]);

  // Map labels to InlineAutosuggest items
  // Show creatable option only if labels array is empty (0 items)
  // API returns labels as string array: ["Mobile Number", "Sales Team Numbers", ...]
  const labelItems = useMemo<InlineAutosuggestItem[]>(() => {
    if (!labelsList || labelsList.length === 0) {
      // Return empty array to allow creating new labels when no labels exist
      return [];
    }
    return (labelsList || []).map((label: string) => ({
      name: label || '',
    }));
  }, [labelsList]);

  const handleSave = () => {
    if (selectedLabelName?.trim() && activeNumberSettings?.id) {
      onSave(activeNumberSettings.id, selectedLabelName.trim());
    }
  };

  const isLoading = getIsRequestPending(getLabelsListRequestStatus?.status);
  const isSaving = getIsRequestPending(setPhoneNumberLabelRequestStatus);

  return (
    <div id="phone-number-label" className="settings-wrapper label-settings">
      <SettingsText title="Phone Number Label" description="Assign a name to your phone number" />
      <div className={`label-settings__input-container ${hasError ? 'label-settings__input-container--error' : ''}`}>
        <InlineAutosuggest
          items={labelItems as InlineAutosuggestItem[]}
          placeholder="Choose existing or type new label name"
          value={selectedLabelName}
          onChange={(text) => {
            const q = (text || '').trim().toLowerCase();
            const hasExact = (labelItems || []).some(
              (t) => (t?.name || '').trim().toLowerCase() === q,
            );
            setSelectedLabelName(text);
            // Only allow new label creation if labels list is empty (0 items)
            // Match reference pattern: isNewColumnName is set when q !== '' && !hasExact
            setIsNewLabel(q !== '' && !hasExact && labelsList?.length === 0);
            // Clear error when user starts typing
          }}
          onPickExisting={(item) => {
            setSelectedLabelName(item?.name || '');
            setIsNewLabel(false);
          }}
          onPickNew={(name) => {
            // Only allow creating new labels if labels list is empty (0 items)
            if (labelsList?.length === 0) {
              setSelectedLabelName(name);
              setIsNewLabel(true);
            }
          }}
          onClear={() => {
            setSelectedLabelName('');
            setIsNewLabel(false);
          }}
          onBlur={() => {
            validateLabelName(selectedLabelName || '');
          }}
          className="label-settings__autosuggest"
          createText="Add"
        />
      {hasError && (
        <div className="label-settings__error-message">
          {hasError ? 'Label length must be between 2 and 30 characters' : ''}
        </div>
      )}
      </div>
      <div className="label-settings__actions">
        <Button
          variant="primary"
          onClick={handleSave}
          className="settings-wrapper__save-btn label-settings__save-btn"
          disabled={hasError || !selectedLabelName?.trim() || isLoading || selectedLabelName?.trim() === activeNumberSettings?.friendlyName || isSaving}
          isLoading={isSaving}
          loadingText="Save"
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default LabelSettings;